import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  PromoterSessionError,
  clearPromoterToken,
  fetchPromoterMe,
  getPromoterToken,
  promoterLogin,
  promoterRegister,
  setPromoterToken,
} from '../services/promoterApi';

const PromoterAuthContext = createContext();

export const usePromoterAuth = () => {
  const context = useContext(PromoterAuthContext);
  if (!context) {
    throw new Error('usePromoterAuth debe ser usado dentro de PromoterAuthProvider');
  }
  return context;
};

// Sesión del promotor PERSONA. Solo el token se guarda (`promoter-token`); el
// perfil (estado, porcentaje, código) se pide a GET /api/promoter/me en cada
// carga. Una empresa que opera como promotora NO pasa por aquí: usa su token
// de empresa (ver pages/promoter/PromoterHome.jsx).
export const PromoterAuthProvider = ({ children }) => {
  const [me, setMe] = useState(null); // { promoter, links, canOperate }
  const [loading, setLoading] = useState(() => Boolean(getPromoterToken()));
  // motivo por el que se perdió la sesión, para mostrarlo en el login
  const [sessionNotice, setSessionNotice] = useState('');
  // hay token pero no se pudo consultar /me (sin conexión)
  const [connectionError, setConnectionError] = useState('');
  const mounted = useRef(true);

  // StrictMode (desarrollo) monta, desmonta y vuelve a montar: hay que volver a marcarlo como montado
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const endSession = useCallback((notice = '') => {
    clearPromoterToken();
    setMe(null);
    setSessionNotice(notice);
    setConnectionError('');
  }, []);

  // Refresca el perfil. `promoter_session_invalid` (o JWT vencido) borra el
  // token y devuelve false: las pantallas mandan al login con el motivo.
  const refresh = useCallback(async () => {
    if (!getPromoterToken()) {
      endSession();
      return false;
    }
    try {
      const data = await fetchPromoterMe('persona');
      if (!mounted.current) return true;
      setMe({ promoter: data.promoter, links: data.links, canOperate: data.canOperate !== false });
      setConnectionError('');
      return true;
    } catch (error) {
      if (error instanceof PromoterSessionError) {
        if (mounted.current) endSession(error.message);
        return false;
      }
      if (mounted.current) setConnectionError(error.message);
      throw error;
    }
  }, [endSession]);

  useEffect(() => {
    if (!getPromoterToken()) {
      setLoading(false);
      return;
    }
    refresh()
      .catch(() => {
        // sin conexión: queda `connectionError` y la pantalla ofrece reintentar
      })
      .finally(() => { if (mounted.current) setLoading(false); });
  }, [refresh]);

  const applySession = (data) => {
    setPromoterToken(data.token);
    setMe({
      promoter: data.promoter,
      links: { pending: 0, accepted: 0, rejected: 0, revoked: 0 },
      canOperate: data.promoter && data.promoter.status === 'approved',
    });
    setSessionNotice('');
    setConnectionError('');
  };

  const login = async (email, password) => {
    try {
      const data = await promoterLogin(email, password);
      applySession(data);
      return { success: true, promoter: data.promoter };
    } catch (error) {
      return { success: false, error: error.message, code: error.code };
    }
  };

  const register = async (payload) => {
    try {
      const data = await promoterRegister(payload);
      applySession(data);
      return { success: true, promoter: data.promoter, message: data.message };
    } catch (error) {
      return { success: false, error: error.message, code: error.code, status: error.status };
    }
  };

  const logout = () => endSession();

  const value = {
    promoter: me ? me.promoter : null,
    me,
    loading,
    sessionNotice,
    connectionError,
    login,
    register,
    logout,
    refresh,
    endSession,
    isAuthenticated: Boolean(me),
  };

  return (
    <PromoterAuthContext.Provider value={value}>
      {children}
    </PromoterAuthContext.Provider>
  );
};
